import { useEffect, useState, useMemo } from "react"
import { useSwipeable } from "react-swipeable"
import logo from "../../../assets/funki_VAG_1280_red.png"
import { Grid, Stack, useMediaQuery, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Box } from "@mui/material"
import { SONG_CATEGORIES, SONG_TYPES, Song } from "../../../utils/types"
import SongCard from "../../../components/Card"
import { useCurrentUser } from "../../../hooks/contexts/currentUserContext"
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted"
import GridViewIcon from "@mui/icons-material/GridView"
import { ChangeCurrentSong, useChangeCurrentSong, useCurrentSong } from "../../../hooks/contexts/currentSongContext"

import { useContext } from "react"
import { PolyglotContext } from "../../../polyglot/polyGlotContext"

interface DisplayedCards {
    displayedSongs: Song[]
    page: number
    cardsPerPage: number
    viewMode: "grid" | "list"
    changeCurrentSong: ChangeCurrentSong
    currentSong: Song | null | undefined
    type: SONG_TYPES
}

const DisplayedCards = ({ displayedSongs, page, cardsPerPage, viewMode, changeCurrentSong, currentSong, type }: DisplayedCards) => {
    const [currentCards, setCurrentCards] = useState<Song[]>(displayedSongs)

    useEffect(() => {
        setCurrentCards(displayedSongs)
    }, [displayedSongs, page, cardsPerPage])

    return type === SONG_TYPES.KEY ? (
        <KeytoneList currentCards={currentCards} changeCurrentSong={changeCurrentSong} currentSong={currentSong} />
    ) : viewMode === "list" ? (
        <ListView currentCards={currentCards} changeCurrentSong={changeCurrentSong} currentSong={currentSong} />
    ) : (
        <Grid container spacing={3} sx={{ height: "auto", alignItems: "space-between" }}>
            {currentCards.map((song) => (
                <Grid key={song.id} item xs={12} sm={6} md={6} lg={4} xl={3}>
                    <SongCard song={song} currentSong={currentSong} changeCurrentSong={changeCurrentSong} />
                </Grid>
            ))}
        </Grid>
    )
}

interface IListView {
    currentCards: Song[]
    changeCurrentSong: ChangeCurrentSong
    currentSong: Song | null | undefined
}

const ListView = ({ currentCards, changeCurrentSong, currentSong }: IListView) => {
    const { polyglot } = useContext(PolyglotContext)

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{polyglot.t("library.displayedCards.tableCellName")}</TableCell>
                        <TableCell>{polyglot.t("library.displayedCards.tableCellKey")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {currentCards.map((song) => {
                        const isSelected = currentSong?.id === song.id
                        return (
                            <TableRow
                                key={song.id}
                                onClick={() => changeCurrentSong({ songId: song.id })}
                                sx={{ cursor: "pointer", background: isSelected ? (theme) => theme.palette.success.main : "inherit" }}
                            >
                                <TableCell sx={{ width: "50%" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <img
                                            src={song.img ?? logo}
                                            alt={song.title}
                                            style={{ width: 50 }}
                                            onError={(e) => {
                                                e.currentTarget.src = "/favicon.png"
                                            }}
                                        />
                                        {song.title}
                                    </Box>
                                </TableCell>
                                {song.keytone ? <TableCell sx={{ width: "50%" }}>{song.keytone}</TableCell> : <TableCell> </TableCell>}    
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

interface KeyView {
    currentCards: Song[]
    changeCurrentSong: ChangeCurrentSong
    currentSong: Song | null | undefined
}

const KeytoneList = ({ currentCards, changeCurrentSong, currentSong }: KeyView) => {
    const { polyglot } = useContext(PolyglotContext)

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{polyglot.t("library.displayedCards.tableCellKey")}</TableCell>
                        <TableCell>{polyglot.t("library.displayedCards.tableCellScale")}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {currentCards.map((song) => {
                        const isSelected = currentSong?.id === song.id
                        return (
                            <TableRow
                                key={song.id}
                                onClick={() => changeCurrentSong({ songId: song.id, songType: SONG_TYPES.KEY })}
                                sx={{ cursor: "pointer", background: isSelected ? (theme) => theme.palette.success.main : "inherit" }}
                            >
                                <TableCell>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{song.keytone}</Box>
                                </TableCell>
                                <TableCell>{song.category.charAt(0).toLocaleUpperCase() + song.category.slice(1)}</TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default DisplayedCards
